import { PrismaEnums } from '@airreading/schema/browser';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { CSVUploadResponse } from '../csv.js';
import { BoundComparatorOps, comparatorFromRanking } from '../ordering/index.js';
import { dateStringOrInstant } from '../zod-utils.js';
import { classTimeSchema } from './class-time.js';
import { learningPathSchema } from './learning-path.js';
import { lessonSchema } from './lesson.js';
import {
  DEPRECATED_pagingSchemaWithMaxPageSize,
  filteringSchemaFrom,
  largePagingSchema,
  pagingSchema,
  sortingSchemaFrom,
  sortOrderSchemaFrom,
} from './modifier.js';
import type { SchoolReport } from './school-report.js';
import { minimalSchoolSchema, schoolSchema } from './school-shared.js';
import { orgFeedbackTypeSchema } from './org-shared.js';
import { completedGLADateEntrySchema, onDemandGLAWithSessionFeedbackSchema } from './session-feedbacks.js';
import { sessionStatusSchema } from './session-shared.js';
import { studentFeedbackIssueSchema, studentFeedbackStatusSchema } from './student-feedback-shared.js';
import { updateUserIdentitiesOptionsSchema, userIdentitySchema } from './user-identity.js';
import { userLikeSchema, userSchema } from './user.js';
import { userRoleAssignmentSchema, userRoleWithSchoolSchema } from './user-role.js';
import { paginatedResponse } from './util-schema-types.js';

export const { StudentYear } = PrismaEnums;
export const studentYearSchema = z.nativeEnum(StudentYear);
export const studentYearNullableSchema = studentYearSchema.nullable();
export type StudentYear = z.infer<typeof studentYearNullableSchema>;

export const studentYearInputSchema = z.enum([
  'Pre_K',
  'K',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
]);

export const studentYearInputSchemaEnum = studentYearInputSchema.enum;

export const studentYearInputSchemaMap = {
  [studentYearInputSchemaEnum.Pre_K]: StudentYear.Pre_K,
  [studentYearInputSchemaEnum.K]: StudentYear.K,
  [studentYearInputSchemaEnum[1]]: StudentYear.first,
  [studentYearInputSchemaEnum[2]]: StudentYear.second,
  [studentYearInputSchemaEnum[3]]: StudentYear.third,
  [studentYearInputSchemaEnum[4]]: StudentYear.fourth,
  [studentYearInputSchemaEnum[5]]: StudentYear.fifth,
  [studentYearInputSchemaEnum[6]]: StudentYear.sixth,
  [studentYearInputSchemaEnum[7]]: StudentYear.seventh,
  [studentYearInputSchemaEnum[8]]: StudentYear.eighth,
  [studentYearInputSchemaEnum[9]]: StudentYear.ninth,
  [studentYearInputSchemaEnum[10]]: StudentYear.tenth,
  [studentYearInputSchemaEnum[11]]: StudentYear.eleventh,
  [studentYearInputSchemaEnum[12]]: StudentYear.twelfth,
  [studentYearInputSchemaEnum[13]]: StudentYear.thirteenth,
} as const;

/**
 * Although Prisma exposes StudentStatus via $Enums, it is not reliably available
 * in all environments, it is defined manually here to ensure access.
 */
export const studentStatusSchema = z.enum(['enrolled', 'dropped', 'program_concluded']);
export type StudentStatus = z.infer<typeof studentStatusSchema>;
export const StudentStatus = studentStatusSchema.enum;

export const StudentStatusLabelMap: Record<StudentStatus, string> = {
  [StudentStatus.enrolled]: 'Enrolled',
  [StudentStatus.dropped]: 'Dropped',
  [StudentStatus.program_concluded]: 'Program concluded',
};

export const studentTagSchema = z.enum(['obc', 'eir', 'hdt']);
export type StudentTag = z.infer<typeof studentTagSchema>;
export const StudentTag = studentTagSchema.enum;

export function studentTagLabel(tag: StudentTag): string {
  switch (tag) {
    case 'obc':
      return 'OBC';
    case 'eir':
      return 'EIR';
    case 'hdt':
      return 'HDT';
  }
}

/** User-facing tag labels, comma-separated. Empty when the student has no tags. */
export function formatStudentTagList(tags: readonly StudentTag[] | null | undefined): string {
  return (tags ?? []).map(studentTagLabel).join(', ');
}

export const glaFollowUpStatusSchema = z.enum(['need_follow_up', 'check_again', 'resolved']);
export type GlaFollowUpStatus = z.infer<typeof glaFollowUpStatusSchema>;
export const GlaFollowUpStatus = glaFollowUpStatusSchema.enum;

/**
 * This type is needed because <Select> (and by extension, <FormSingleSelect>)
 * only supports string values while still reserving the empty string as its
 * own sentinel value for `undefined`. So we need to reserve the `'null'`
 * literal as a sentinel value to represent actual `null`.
 */
export const studentYearSelectableSchema = studentYearNullableSchema.unwrap().or(z.literal('null'));
export type StudentYearSelectable = z.infer<typeof studentYearSelectableSchema>;

export const StudentYearsInOrder: NonNullable<StudentYear>[] = [
  'Pre_K',
  'K',
  'first',
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
  'seventh',
  'eighth',
  'ninth',
  'tenth',
  'eleventh',
  'twelfth',
  'thirteenth',
];

export const StudentYearColorMap: Record<NonNullable<StudentYear>, string> = {
  [StudentYear.Pre_K]: '#FEF3C7',
  [StudentYear.K]: '#ECFCCB',
  [StudentYear.first]: '#FFEDD5',
  [StudentYear.second]: '#DCFCE7',
  [StudentYear.third]: '#E2E8F0',
  [StudentYear.fourth]: '#E0E7FF',
  [StudentYear.fifth]: '#FFE4E6',
  [StudentYear.sixth]: '#F3E8FF',
  [StudentYear.seventh]: '#FEF3C7',
  [StudentYear.eighth]: '#ECFCCB',
  [StudentYear.ninth]: '#FFEDD5',
  [StudentYear.tenth]: '#DCFCE7',
  [StudentYear.eleventh]: '#E2E8F0',
  [StudentYear.twelfth]: '#E0E7FF',
  [StudentYear.thirteenth]: '#FFE4E6',
};

export const byStudentYear = comparatorFromRanking(StudentYearsInOrder);

class StudentYearCompareOps extends BoundComparatorOps<NonNullable<StudentYear>> {
  isBefore = this.lt;
  isAfter = this.gt;
  isOnOrBefore = this.lte;
  isOnOrAfter = this.gte;
}

export const StudentYearCompare = (a: NonNullable<StudentYear>) => new StudentYearCompareOps(byStudentYear, a);

export const studentYearValues: StudentYearSelectable[] = ['null', ...Object.values(StudentYear)];
export function studentYearOption(value: StudentYearSelectable | null | undefined): string {
  switch (value) {
    case undefined:
    case null:
    case 'null':
      return '(empty)';
    case 'Pre_K':
      return 'Pre-K';
    case 'K':
      return 'K';
    case 'first':
      return '1st';
    case 'second':
      return '2nd';
    case 'third':
      return '3rd';
    case 'fourth':
      return '4th';
    case 'fifth':
      return '5th';
    case 'sixth':
      return '6th';
    case 'seventh':
      return '7th';
    case 'eighth':
      return '8th';
    case 'ninth':
      return '9th';
    case 'tenth':
      return '10th';
    case 'eleventh':
      return '11th';
    case 'twelfth':
      return '12th';
    case 'thirteenth':
      return '13th';
  }
}

export function studentYearOptionOnlyNumber(value: StudentYearSelectable | null | undefined): string {
  switch (value) {
    case undefined:
    case null:
    case 'null':
      return '(empty)';
    case 'Pre_K':
      return 'PreK';
    case 'K':
      return 'K';
    case 'first':
      return '1';
    case 'second':
      return '2';
    case 'third':
      return '3';
    case 'fourth':
      return '4';
    case 'fifth':
      return '5';
    case 'sixth':
      return '6';
    case 'seventh':
      return '7';
    case 'eighth':
      return '8';
    case 'ninth':
      return '9';
    case 'tenth':
      return '10';
    case 'eleventh':
      return '11';
    case 'twelfth':
      return '12';
    case 'thirteenth':
      return '13';
  }
}

export const studentContactTypeSchema = z.enum(['school_parent', 'family_parent']);
export type StudentContactType = z.infer<typeof studentContactTypeSchema>;
export const StudentContactType = studentContactTypeSchema.enum;

export const studentContactSchema = z.object({
  type: studentContactTypeSchema,
  name: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
});
export type StudentContact = z.infer<typeof studentContactSchema>;

export const studentShortLinkSchema = z.object({
  shortCode: z.string(),
  isActive: z.boolean(),
  isPaused: z.boolean(),
});
export type StudentShortLink = z.infer<typeof studentShortLinkSchema>;

export function sessionAttendanceOption(row: SchoolReport | null | undefined, timeZone: string, url: string): string {
  if (!row?.sessions || row.sessions.length === 0) {
    return '0/0';
  }
  const rowReturn = row.sessions.map((session) => {
    const zoned = session.startInstant.toZonedDateTimeISO(timeZone);
    const { year: isoYear, month: isoMonth, day: isoDay } = zoned.toPlainDate();
    const sessionDate = `${isoMonth.toString().padStart(2, '0')}/${isoDay.toString().padStart(2, '0')}/${isoYear}`;
    const sessionStudentFeedback = session.sessionFeedbacks
      ?.map((feedback) => {
        return `${url}/${session.id}__${feedback.studentId}__${feedback.coachId}`;
      })
      .join(', ');
    return `${sessionDate} ${sessionStudentFeedback}`;
  });

  return rowReturn.join('\n');
}

export function maxBookPartForStudentGrade(value: StudentYearSelectable): number {
  switch (value) {
    case undefined:
    case null:
    case 'null':
      return 1;
    case StudentYear.Pre_K:
    case StudentYear.K:
      return 2;
    case StudentYear.first:
      return 2;
    case StudentYear.second:
      return 3;
    case StudentYear.third:
      return 4;
    case StudentYear.fourth:
      return 5;
    case StudentYear.fifth:
    case StudentYear.sixth:
      return 6;
    case StudentYear.seventh:
    case StudentYear.eighth:
    case StudentYear.ninth:
    case StudentYear.tenth:
    case StudentYear.eleventh:
    case StudentYear.twelfth:
    case StudentYear.thirteenth:
      return 6;
    default:
      return 1;
  }
}

export const studentSchema = z.object({
  id: z.string(),
  grade: studentYearNullableSchema,
  specialEd: z.boolean(),
  dyslexic: z.boolean().nullable(),
  ell: z.boolean().nullable(),
  learningPathId: z.string().nullable(),
  startingLessonId: z.string().nullable(),
  requestedClassTimeId: z.string().nullable(),
  status: studentStatusSchema.nullable(),
  fleid: z.string().nullable(),
  classroomTeacher: z.string().nullable(),
  phonicsSkill1Mastered: z.number().nullable(),
  phonicsSkill2Mastered: z.number().nullable(),
  phonicsSkill3Mastered: z.number().nullable(),
  phonicsSkill4Mastered: z.number().nullable(),
  morphemeSkill1Mastered: z.number().nullable(),
  morphemeSkill2Mastered: z.number().nullable(),
  morphemeSkill3Mastered: z.number().nullable(),
  averageAttendanceLast14Days: z.number().nullable(),
  averageAttendanceLast14DaysCalculatedAt: z.instanceof(Temporal.Instant).nullable(),
  studentTags: z.array(studentTagSchema).nullable(),
  glaFollowUpStatus: glaFollowUpStatusSchema.nullable(),
  glaReviewNotes: z.string().nullable(),
});
export type Student = z.infer<typeof studentSchema>;

export const studentWithScheduleInfoSchema = studentSchema.extend({
  learningPath: z.lazy(() => learningPathSchema.nullable()),
  startingLesson: z.lazy(() => lessonSchema.nullable()),
  requestedClassTime: z.lazy(() => classTimeSchema.nullable()),
});
export type StudentWithScheduleInfo = z.infer<typeof studentWithScheduleInfoSchema>;

export const createStudentOptionsSchema = z.object({
  grade: studentYearNullableSchema,
  specialEd: z.boolean().optional(),
  dyslexic: z.boolean().optional(),
  ell: z.boolean().optional(),
  fleid: z.string().nullable().optional(),
  classroomTeacher: z.string().nullable().optional(),
  studentTags: z.array(studentTagSchema).optional(),
  // wrap in a `level` object since both ids need to be passed together or not at all
  level: z
    .object({
      learningPathId: z.string(),
      startingLessonId: z.string(),
    })
    .optional(),
  requestedClassTimeId: z.string().optional(),
});
export type CreateStudentOptions = z.infer<typeof createStudentOptionsSchema>;

export const updateStudentOptionsSchema = z.object({
  user: z
    .object({
      firstName: z.string().optional(),
      lastName: z.string().optional().nullable(),
      timeZone: z.string().optional(),
      email: z.string().nullable().optional(),
    })
    .optional()
    .nullable(),
  roles: z.array(userRoleAssignmentSchema).optional().nullable(),
  grade: studentYearNullableSchema.optional(),
  status: studentStatusSchema.optional().nullable(),
  specialEd: z.boolean().optional(),
  dyslexic: z.boolean().optional(),
  ell: z.boolean().optional(),
  fleid: z.string().optional().nullable(),
  classroomTeacher: z.string().optional().nullable(),
  edlinkId: z.string().optional().nullable(),
  studentTags: z.array(studentTagSchema).optional(),
  userIdentities: updateUserIdentitiesOptionsSchema.optional(),
  // wrap in a `level` object since both ids need to be passed together or not at all
  level: z
    .object({
      learningPathId: z.string(),
      startingLessonId: z.string(),
    })
    .optional()
    .nullable(),
  requestedClassTimeId: z.string().optional().nullable(),
  phonicsSkill1Mastered: z.number().optional().nullable(),
  phonicsSkill2Mastered: z.number().optional().nullable(),
  phonicsSkill3Mastered: z.number().optional().nullable(),
  phonicsSkill4Mastered: z.number().optional().nullable(),
  morphemeSkill1Mastered: z.number().optional().nullable(),
  morphemeSkill2Mastered: z.number().optional().nullable(),
  morphemeSkill3Mastered: z.number().optional().nullable(),
  studentContacts: z.array(studentContactSchema).optional().nullable(),
});
export type UpdateStudentOptions = z.infer<typeof updateStudentOptionsSchema>;

export const studentOfSchoolSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  specialEd: z.boolean().nullable(),
  dyslexic: z.boolean().nullable(),
  ell: z.boolean().nullable(),
  grade: studentYearNullableSchema,
  learningPath: z.lazy(() => learningPathSchema.nullable()),
  startingLesson: z.lazy(() => lessonSchema.nullable()),
  nextLesson: z.lazy(() => lessonSchema.nullable()),
  endLesson: z.lazy(() => lessonSchema.nullable()),
  requestedClassTime: z.lazy(() => classTimeSchema.nullable()),
  totalFutureSessions: z.number(),
  status: studentStatusSchema.nullable(),
  nextSession: z
    .object({
      sessionId: z.string(),
      lessonId: z.string(),
      lessonName: z.string(),
      startInstant: z.instanceof(Temporal.Instant),
    })
    .nullable(),
  shortCode: z.string().nullable(),
  shortLinks: z.array(studentShortLinkSchema).optional(),
  edlinkId: z.string().nullable().optional(),
});
export type StudentOfSchool = z.infer<typeof studentOfSchoolSchema>;

export const getStudentsOfSchoolResultsSchema = paginatedResponse.extend({
  students: z.array(studentOfSchoolSchema),
});
export type GetStudentsOfSchoolResults = z.infer<typeof getStudentsOfSchoolResultsSchema>;

/** School students Learning paths filter value for `Student.learningPathId IS NULL`. */
export const UNASSIGNED_LEARNING_PATH_FILTER_ID = '-';

export type LearningPathIdFilter =
  | { learningPathId: null }
  | { learningPathId: { in: string[] } }
  | { OR: [{ learningPathId: null }, { learningPathId: { in: string[] } }] };

/** Prisma-shaped filter for the school students Learning paths multi-select, including the empty option. */
export function learningPathIdFilterFromSelection(
  learningPathIds: string[] | undefined,
): LearningPathIdFilter | undefined {
  if (!learningPathIds || learningPathIds.length === 0) {
    return undefined;
  }

  const includeUnassigned = learningPathIds.includes(UNASSIGNED_LEARNING_PATH_FILTER_ID);
  const ids = learningPathIds.filter((id) => id !== UNASSIGNED_LEARNING_PATH_FILTER_ID);

  if (includeUnassigned && ids.length > 0) {
    return { OR: [{ learningPathId: null }, { learningPathId: { in: ids } }] };
  }
  if (includeUnassigned) {
    return { learningPathId: null };
  }
  if (ids.length > 0) {
    return { learningPathId: { in: ids } };
  }
  return undefined;
}

export const getStudentsOfSchoolFilterSchema = z.object({
  query: z.string().optional(),
  grades: z.array(studentYearNullableSchema).optional(),
  startingLessons: z.array(z.string()).optional(),
  classTimeIds: z.array(z.string()).optional(),
  learningPathIds: z.array(z.string()).optional(),
  statuses: z.array(studentStatusSchema).optional(),
  hasEdlinkId: z.boolean().optional(),
});
export type GetStudentsOfSchoolFilter = z.infer<typeof getStudentsOfSchoolFilterSchema>;

export const getStudentsOfSchoolSortableColumnSchema = studentOfSchoolSchema
  .keyof()
  .extract(['grade', 'learningPath', 'startingLesson', 'totalFutureSessions', 'nextLesson']);
export type GetStudentsOfSchoolSortableColumn = z.infer<typeof getStudentsOfSchoolSortableColumnSchema>;

export const getStudentsOfSchoolSortOrderSchema = sortOrderSchemaFrom(getStudentsOfSchoolSortableColumnSchema);
export type GetStudentsOfSchoolSortOrder = z.infer<typeof getStudentsOfSchoolSortOrderSchema>;

export const getStudentsOfSchoolModifierSchema = largePagingSchema
  .merge(filteringSchemaFrom(getStudentsOfSchoolFilterSchema))
  .merge(sortingSchemaFrom(getStudentsOfSchoolSortableColumnSchema));
export type GetStudentsOfSchoolModifier = z.infer<typeof getStudentsOfSchoolModifierSchema>;

export const studentReviewHistorySchema = z.object({
  dateInstant: z.instanceof(Temporal.Instant),
  description: z.string().nullable(),
  studentId: z.string(),
  coach: z.lazy(() => userSchema),
  lesson: z.lazy(() => lessonSchema).nullable(),
  issue: studentFeedbackIssueSchema.nullable(),
});
export type StudentReviewHistory = z.infer<typeof studentReviewHistorySchema>;

export const getStudentReviewHistoryModifierSchema = pagingSchema;
export type GetStudentReviewHistoryModifier = z.infer<typeof getStudentReviewHistoryModifierSchema>;

export const getStudentReviewHistoryResultsSchema = paginatedResponse.extend({
  reviewHistory: z.array(studentReviewHistorySchema),
});
export type GetStudentReviewHistoryResults = z.infer<typeof getStudentReviewHistoryResultsSchema>;

export const studentLikeSchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  fullNameOrEmail: z.string().nullable(),
});

export const studentSessionLikeSchema = z.object({
  sessionId: z.string(),
  studentId: z.string(),
  coachId: z.string().nullable(),
  joined: z.boolean(),
  startInstant: z.instanceof(Temporal.Instant),
  endInstant: z.instanceof(Temporal.Instant),
  lesson: z.lazy(() => lessonSchema).nullable(),
});
export type StudentSessionLike = z.infer<typeof studentSessionLikeSchema>;

export const studentSessionFeedbackLikeSchema = z.object({
  sessionId: z.string(),
  studentId: z.string(),
  coachId: z.string(),
  issues: z.array(z.string()),
  studentAttended: z.boolean().nullable(),
  startInstant: z.instanceof(Temporal.Instant),
  endInstant: z.instanceof(Temporal.Instant),
  status: sessionStatusSchema,
});
export type StudentSessionFeedbackLike = z.infer<typeof studentSessionFeedbackLikeSchema>;

/** School portal roster row — simple fields for school-admin students list */
export const schoolPortalStudentSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  status: studentStatusSchema.nullable(),
  grade: studentYearNullableSchema,
  requestedClassTime: z.lazy(() => classTimeSchema.nullable()),
  classroomTeacher: z.string().nullable(),
  email: z.string().nullable(),
  fleid: z.string().nullable(),
  sessionFeedbacks: z.array(studentSessionFeedbackLikeSchema).nullable(),
});
export type SchoolPortalStudent = z.infer<typeof schoolPortalStudentSchema>;

/**
 * School portal "Student ID" column: email when present, otherwise state ID
 * (FLEID today; Louisiana ID can be added when that field exists).
 */
export function getSchoolPortalStudentIdDisplay({
  email,
  fleid,
}: Pick<SchoolPortalStudent, 'email' | 'fleid'>): string | null {
  if (email) return email;
  if (fleid) return fleid;
  return null;
}

/**
 * Admin "State ID" column value. FLEID is the only supported format today;
 * additional state ID fields can be preferred here when added.
 */
export function getStudentStateIdDisplay(student: { fleid?: string | null }): string | null {
  if (student.fleid) return student.fleid;
  return null;
}

export const getSchoolPortalStudentsFilterSchema = z.object({
  query: z.string().optional(),
  grades: z.array(studentYearNullableSchema).optional(),
  statuses: z.array(studentStatusSchema).optional(),
  classTimeIds: z.array(z.string()).optional(),
  classroomTeachers: z.array(z.string()).optional(),
});
export type GetSchoolPortalStudentsFilter = z.infer<typeof getSchoolPortalStudentsFilterSchema>;

export const getSchoolPortalStudentsSortableColumnSchema = z.enum([
  'firstName',
  'lastName',
  'grade',
  'attendedSessionsOverall',
  'attendancePercentOverall',
  'attendancePercentPast14Days',
]);
export type GetSchoolPortalStudentsSortableColumn = z.infer<typeof getSchoolPortalStudentsSortableColumnSchema>;

export const getSchoolPortalStudentsSortOrderSchema = sortOrderSchemaFrom(getSchoolPortalStudentsSortableColumnSchema);
export type GetSchoolPortalStudentsSortOrder = z.infer<typeof getSchoolPortalStudentsSortOrderSchema>;

export const getSchoolPortalStudentsModifierSchema = DEPRECATED_pagingSchemaWithMaxPageSize.merge(
  filteringSchemaFrom(getSchoolPortalStudentsFilterSchema),
).merge(sortingSchemaFrom(getSchoolPortalStudentsSortableColumnSchema));
export type GetSchoolPortalStudentsModifier = z.infer<typeof getSchoolPortalStudentsModifierSchema>;

export const schoolPortalStudentFilterOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
});
export type SchoolPortalStudentFilterOption = z.infer<typeof schoolPortalStudentFilterOptionSchema>;

export const getSchoolPortalStudentsResultsSchema = paginatedResponse.extend({
  students: z.array(schoolPortalStudentSchema),
  filterOptions: z.object({
    classTimes: z.array(schoolPortalStudentFilterOptionSchema),
    classroomTeachers: z.array(z.string()),
  }),
});
export type GetSchoolPortalStudentsResults = z.infer<typeof getSchoolPortalStudentsResultsSchema>;

/** School portal KPI summary for school-admin students page */
export const schoolPortalStudentStatsSchema = z.object({
  totalCurrentStudents: z.number().int().nonnegative(),
  averageAttendancePast14Days: z.number().nullable(),
  averageAttendanceOverall: z.number().nullable(),
});
export type SchoolPortalStudentStats = z.infer<typeof schoolPortalStudentStatsSchema>;

export const fullStudentSchema = studentLikeSchema.extend({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  timeZone: z.string().nullable(),
  grade: studentYearNullableSchema,
  status: studentStatusSchema.nullable(),
  specialEd: z.boolean().nullable(),
  dyslexic: z.boolean().nullable(),
  ell: z.boolean().nullable(),
  fleid: z.string().nullable(),
  classroomTeacher: z.string().nullable(),
  edlinkId: z.string().nullable(),
  userIdentities: z.array(userIdentitySchema),
  phonicsSkill1Mastered: z.number().nullable(),
  phonicsSkill2Mastered: z.number().nullable(),
  phonicsSkill3Mastered: z.number().nullable(),
  phonicsSkill4Mastered: z.number().nullable(),
  morphemeSkill1Mastered: z.number().nullable(),
  morphemeSkill2Mastered: z.number().nullable(),
  morphemeSkill3Mastered: z.number().nullable(),
  userRoles: z.array(userRoleWithSchoolSchema).nullable(),
  school: z.lazy(() => schoolSchema.nullable()),
  learningPath: z.lazy(() => learningPathSchema.nullable()),
  startingLesson: z.lazy(() => lessonSchema.nullable()),
  requestedClassTime: z.lazy(() => classTimeSchema.nullable()),
  studentSessions: z.array(studentSessionLikeSchema).nullable(),
  sessionFeedbacks: z.array(studentSessionFeedbackLikeSchema).nullable(),
  totalFutureSessions: z.number().nullable(),
  nextSessionPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  studentTags: z.array(studentTagSchema).nullable(),
  shortCode: z.string().nullable(),
  shortLinks: z.array(studentShortLinkSchema).optional(),
  studentContacts: z.array(studentContactSchema).nullable(),
  canLaunchManualGLA: z.boolean(),
});
export type FullStudent = z.infer<typeof fullStudentSchema>;

/** Admin students list (`GET /students`) — view-specific contract; narrower than {@link FullStudent}. */
export const studentListRowSchema = studentLikeSchema.extend({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  grade: studentYearNullableSchema,
  status: studentStatusSchema.nullable(),
  specialEd: z.boolean().nullable(),
  dyslexic: z.boolean().nullable(),
  ell: z.boolean().nullable(),
  /** State ID — FLEID today; keep nullable for students without a state ID. */
  fleid: z.string().nullable(),
  phonicsSkill1Mastered: z.number().nullable(),
  phonicsSkill2Mastered: z.number().nullable(),
  phonicsSkill3Mastered: z.number().nullable(),
  morphemeSkill1Mastered: z.number().nullable(),
  morphemeSkill2Mastered: z.number().nullable(),
  school: minimalSchoolSchema.nullable(),
  studentSessions: z.array(studentSessionLikeSchema).nullable(),
  sessionFeedbacks: z.array(studentSessionFeedbackLikeSchema).nullable(),
  totalFutureSessions: z.number().nullable(),
  nextSessionPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  shortCode: z.string().nullable(),
  canLaunchManualGLA: z.boolean(),
});
export type StudentListRow = z.infer<typeof studentListRowSchema>;

const namedLearningPathSchema = z.object({
  id: z.string(),
  name: z.string(),
});

const namedLessonSchema = z.object({
  id: z.string(),
  name: z.string(),
});

/** Admin no-future-sessions roster (`GET /students/no-future-sessions`). */
export const studentNoFutureSessionsRowSchema = studentLikeSchema.extend({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  grade: studentYearNullableSchema,
  status: studentStatusSchema.nullable(),
  totalFutureSessions: z.number().nullable(),
  school: minimalSchoolSchema.nullable(),
  learningPath: namedLearningPathSchema.nullable(),
  startingLesson: namedLessonSchema.nullable(),
  studentSessions: z.array(studentSessionLikeSchema).nullable(),
  sessionFeedbacks: z.array(studentSessionFeedbackLikeSchema).nullable(),
});
export type StudentNoFutureSessionsRow = z.infer<typeof studentNoFutureSessionsRowSchema>;

export const namedLikeSchema = z.object({
  id: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});
export type NamedLike = z.infer<typeof namedLikeSchema>;

export const namedLikeWithShortCodeSchema = namedLikeSchema.extend({
  shortCode: z.string().nullable(),
});
export type NamedLikeWithShortCode = z.infer<typeof namedLikeWithShortCodeSchema>;

export const getStudentsSortableColumnSchema = z.enum([
  'fullNameOrEmail',
  'totalFutureSessions',
  'grade',
  'school',
  'nextSessionPlainDate',
]);
export type GetStudentsSortableColumn = z.infer<typeof getStudentsSortableColumnSchema>;

export const getStudentSortingSchema = sortingSchemaFrom(getStudentsSortableColumnSchema);

export const getStudentsSortOrderSchema = sortOrderSchemaFrom(getStudentsSortableColumnSchema);
export type GetStudentsSortOrder = z.infer<typeof getStudentsSortOrderSchema>;

export const getStudentsFilterSchema = z.object({
  query: z.string().optional(),
  schools: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  grades: z.array(studentYearNullableSchema).optional(),
  statuses: z.array(studentStatusSchema).optional(),
  sessionsEndDate: z.string().optional(),
  totalFutureSessions: z.array(z.number()).optional(),
  excludeTest: z.boolean().optional(),
  hasPastSessions: z.boolean().optional(),
  learningPathNames: z.array(z.string()).optional(),
  scheduleEndDateAfterToday: z.boolean().optional(),
});
export type GetStudentsFilter = z.infer<typeof getStudentsFilterSchema>;

export const getStudentsModifierSchema = largePagingSchema
  .merge(filteringSchemaFrom(getStudentsFilterSchema))
  .merge(getStudentSortingSchema)
  .extend({
    isAllPages: z.coerce.boolean().optional().default(false),
  });
export type GetStudentsModifier = z.infer<typeof getStudentsModifierSchema>;

/** Session feedback `levels` buckets for the Fluency issue admin list. */
export const fluencyIssueLevelFilterSchema = z.enum(['level_too_easy', 'level_too_hard']);
export type FluencyIssueLevelFilter = z.infer<typeof fluencyIssueLevelFilterSchema>;
export const FluencyIssueLevelFilter = fluencyIssueLevelFilterSchema.enum;

/** Session feedback `issues` (lesson) buckets for the Mastery check issue admin list. */
export const masteryCheckLevelFilterSchema = z.enum(['lesson_too_easy', 'lesson_too_hard']);
export type MasteryCheckLevelFilter = z.infer<typeof masteryCheckLevelFilterSchema>;
export const MasteryCheckLevelFilter = masteryCheckLevelFilterSchema.enum;

export const getStudentsWithLearningIssuesFilterSchema = getStudentsFilterSchema.extend({
  issueStartDateFrom: z.string().optional(),
  issueStartDateTo: z.string().optional(),
  issueStatuses: z.array(studentFeedbackStatusSchema).optional(),
  fluencyIssueLevels: z.array(fluencyIssueLevelFilterSchema).optional(),
  masteryCheckLevels: z.array(masteryCheckLevelFilterSchema).optional(),
});
export type GetStudentsWithLearningIssuesFilter = z.infer<typeof getStudentsWithLearningIssuesFilterSchema>;

export const getStudentsWithLearningIssuesSortableColumnSchema = z.enum(['issueStartDate']);
export type GetStudentsWithLearningIssuesSortableColumn = z.infer<
  typeof getStudentsWithLearningIssuesSortableColumnSchema
>;

export const getStudentsWithLearningIssuesSortOrderSchema = sortOrderSchemaFrom(
  getStudentsWithLearningIssuesSortableColumnSchema,
);
export type GetStudentsWithLearningIssuesSortOrder = z.infer<typeof getStudentsWithLearningIssuesSortOrderSchema>;

export const getStudentsWithLearningIssuesModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getStudentsWithLearningIssuesFilterSchema))
  .merge(sortingSchemaFrom(getStudentsWithLearningIssuesSortableColumnSchema))
  .extend({
    isAllPages: z.coerce.boolean().optional().default(false),
  });
export type GetStudentsWithLearningIssuesModifier = z.infer<typeof getStudentsWithLearningIssuesModifierSchema>;

// Schema for learning issue data
const learningIssueFeedbackDateSchema = z.object({
  date: dateStringOrInstant,
  sessionId: z.string(),
  coachId: z.string(),
});
const learningIssueReviewerSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string(),
});
const learningIssueStudentFeedbackReviewerSchema = z.object({
  id: z.string(),
  fullNameOrEmail: z.string().nullable(),
});
const learningIssueDataSchema = z.object({
  issueStartDate: dateStringOrInstant,
  reviewer: learningIssueReviewerSchema.nullable(),
  issueStatus: studentFeedbackStatusSchema,
  note: z.string(),
  lessonTooEasyCount: z.number(),
  lessonTooEasyDates: z.array(learningIssueFeedbackDateSchema),
  lessonTooHardCount: z.number(),
  lessonTooHardDates: z.array(learningIssueFeedbackDateSchema),
  levelTooEasyCount: z.number(),
  levelTooEasyDates: z.array(learningIssueFeedbackDateSchema),
  levelTooHardCount: z.number(),
  levelTooHardDates: z.array(learningIssueFeedbackDateSchema),
  activeClassIds: z.array(z.string()),
});

/*
 * Feedback payload for the Learning Issue edit modal.
 *
 * Path/lesson/reviewer shapes are inlined (instead of importing other schemas) to avoid
 * circular dependencies:
 *
 * - user.ts imports studentSchema, so student.ts cannot import userSchema
 * - student-feedbacks.ts imports studentSchema, so student.ts cannot import studentFeedbackSchema
 */
const learningIssueStudentFeedbackSchema = z.object({
  id: z.string(),
  studentId: z.string(),
  studentFeedbackIssue: studentFeedbackIssueSchema,
  relevelNeeded: z.boolean(),
  learningPathId: z.string().nullable(),
  startingLessonId: z.string().nullable(),
  reviewerId: z.string(),
  note: z.string(),
  status: studentFeedbackStatusSchema,
  createdAtInstant: z.instanceof(Temporal.Instant),
  learningPath: namedLearningPathSchema.nullable(),
  startingLesson: namedLessonSchema.nullable(),
  studentLearningPath: namedLearningPathSchema.nullable(),
  studentStartingLesson: namedLessonSchema.nullable(),
  reviewer: learningIssueStudentFeedbackReviewerSchema,
});
export type LearningIssueStudentFeedback = z.infer<typeof learningIssueStudentFeedbackSchema>;

/** Admin Learning Issue list (`GET /students/learning-issue`) — view-specific contract. */
export const studentLearningIssueRowSchema = studentLikeSchema.extend({
  grade: studentYearNullableSchema,
  status: studentStatusSchema.nullable(),
  school: minimalSchoolSchema.nullable(),
  totalFutureSessions: z.number().nullable(),
  canLaunchManualGLA: z.boolean(),
  learningIssueData: learningIssueDataSchema.optional(),
  learningIssueStudentFeedback: learningIssueStudentFeedbackSchema.optional(),
  nextScheduledSessionLesson: namedLessonSchema.nullable(),
  nextGLASessionStartInstant: dateStringOrInstant.nullable(),
  completedGLADates: z.array(completedGLADateEntrySchema),
});
export type StudentLearningIssueRow = z.infer<typeof studentLearningIssueRowSchema>;

export const getStudentsWithLearningIssuesResultsSchema = paginatedResponse.extend({
  students: z.array(studentLearningIssueRowSchema),
});
export type GetStudentsWithLearningIssuesResults = z.infer<typeof getStudentsWithLearningIssuesResultsSchema>;

export const getStudentsResultsSchema = paginatedResponse.extend({
  students: z.array(studentListRowSchema),
});
export type GetStudentsResults = z.infer<typeof getStudentsResultsSchema>;

/** Paginated {@link FullStudent} results (dropped, makeup diagnostic, etc.). */
export const getFullStudentsResultsSchema = paginatedResponse.extend({
  students: z.array(fullStudentSchema),
});
export type GetFullStudentsResults = z.infer<typeof getFullStudentsResultsSchema>;

export const getStudentsNoFutureSessionsFilterSchema = z.object({
  query: z.string().optional(),
  schools: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  grades: z.array(studentYearNullableSchema).optional(),
  totalFutureSessions: z.array(z.number()).optional(),
});
export type GetStudentsNoFutureSessionsFilter = z.infer<typeof getStudentsNoFutureSessionsFilterSchema>;

export const getStudentsNoFutureSessionsModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getStudentsNoFutureSessionsFilterSchema))
  .merge(getStudentSortingSchema);
export type GetStudentsNoFutureSessionsModifier = z.infer<typeof getStudentsNoFutureSessionsModifierSchema>;

export const getStudentsNoFutureSessionsResultsSchema = paginatedResponse.extend({
  students: z.array(studentNoFutureSessionsRowSchema),
});
export type GetStudentsNoFutureSessionsResults = z.infer<typeof getStudentsNoFutureSessionsResultsSchema>;

export const getMakeupDiagnosticStudentsFilterSchema = z.object({
  query: z.string().optional(),
  schools: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  grades: z.array(studentYearNullableSchema).optional(),
});
export type GetMakeupDiagnosticStudentsFilter = z.infer<typeof getMakeupDiagnosticStudentsFilterSchema>;

export const getMakeupDiagnosticStudentsSortableColumnSchema = z.enum(['learningPath', 'school']);
export type GetMakeupDiagnosticStudentsSortableColumn = z.infer<typeof getMakeupDiagnosticStudentsSortableColumnSchema>;

export const getMakeupDiagnosticStudentsSortOrderSchema = sortOrderSchemaFrom(
  getMakeupDiagnosticStudentsSortableColumnSchema,
);
export type GetMakeupDiagnosticStudentsSortOrder = z.infer<typeof getMakeupDiagnosticStudentsSortOrderSchema>;

export const getMakeupDiagnosticStudentsModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getMakeupDiagnosticStudentsFilterSchema))
  .merge(sortingSchemaFrom(getMakeupDiagnosticStudentsSortableColumnSchema));
export type GetMakeupDiagnosticStudentsModifier = z.infer<typeof getMakeupDiagnosticStudentsModifierSchema>;

export const studentWithMultipleSchedulesSchema = fullStudentSchema.extend({
  lastLesson: lessonSchema.nullable(),
  totalDifferentClassTimes: z.number().default(0),
});
export type StudentWithMultipleSchedules = z.infer<typeof studentWithMultipleSchedulesSchema>;

export const getStudentsWithMultipleSchedulesResultsSchema = paginatedResponse.extend({
  students: z.array(studentWithMultipleSchedulesSchema),
});
export type GetStudentsWithMultipleSchedulesResults = z.infer<typeof getStudentsWithMultipleSchedulesResultsSchema>;

export const recalculateStudentAttendanceRequestSchema = z.object({
  studentId: z.string(),
});
export type RecalculateStudentAttendanceRequest = z.infer<typeof recalculateStudentAttendanceRequestSchema>;

export const coachViewableStudentSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastInitial: z.string().nullable(),
  grade: studentYearNullableSchema,
  coachFacingDisplayName: z.string().nullable(),
  schoolName: z.string().nullable(),
  orgName: z.string().nullable(),
  /** Org feedback type; `parentFeedback` means notes are shared with the student/family. */
  orgFeedbackType: orgFeedbackTypeSchema.nullable(),
  specialEd: z.boolean().nullable(),
  dyslexic: z.boolean().nullable(),
  ell: z.boolean().nullable(),
  learningPathId: z.string().nullable(),
  startingLessonId: z.string().nullable(),
});
export type CoachViewableStudent = z.infer<typeof coachViewableStudentSchema>;

// Schema for status filter that can be either a simple value or an object with Prisma operators
const statusFilterSchema = z.union([
  studentStatusSchema,
  z.object({
    not: studentStatusSchema,
  }),
  z.object({
    in: z.array(studentStatusSchema),
  }),
]);

export const updateStudentsBulkActionsOperatorSchema = z.enum(['equals', 'notEquals']);
export type UpdateStudentsBulkActionsOperator = z.infer<typeof updateStudentsBulkActionsOperatorSchema>;
export const UpdateStudentsBulkActionsOperator = updateStudentsBulkActionsOperatorSchema.enum;

export const getStudentsCountFilterSchema = getStudentsFilterSchema.extend({
  // Keep these for backward compatibility but mark as deprecated
  validationStatus: studentStatusSchema.optional(),
  validationOperator: updateStudentsBulkActionsOperatorSchema.optional(),
  // New field that accepts Prisma-like status filters
  status: statusFilterSchema.optional(),
});
export type GetStudentsCountFilter = z.infer<typeof getStudentsCountFilterSchema>;

export const getStudentsCountModifierSchema = filteringSchemaFrom(getStudentsCountFilterSchema);
export type GetStudentsCountModifier = z.infer<typeof getStudentsCountModifierSchema>;

export const getStudentsCountResultsSchema = z.object({
  total: z.number(),
  totalMatching: z.number(),
});
export type GetStudentsCountResults = z.infer<typeof getStudentsCountResultsSchema>;

export const updateStudentsBulkActionsSchema = z.enum(['drop', 'conclude', 'enroll']);
export type UpdateStudentsBulkActions = z.infer<typeof updateStudentsBulkActionsSchema>;
export const UpdateStudentsBulkActions = updateStudentsBulkActionsSchema.enum;

export const UPDATE_STUDENTS_BULK_ACTIONS = {
  [UpdateStudentsBulkActions.drop]: {
    delta: { status: StudentStatus.dropped },
    eligibility: { status: StudentStatus.enrolled },
    // validation: (student) => student.status === StudentStatus.enrolled,
  },
  [UpdateStudentsBulkActions.conclude]: {
    delta: { status: StudentStatus.program_concluded },
    eligibility: { status: { not: StudentStatus.program_concluded } },
    // validation: (student) => student.status !== StudentStatus.program_concluded,
  },
  [UpdateStudentsBulkActions.enroll]: {
    delta: { status: StudentStatus.enrolled },
    eligibility: { status: { in: [StudentStatus.dropped, StudentStatus.program_concluded] } },
    // validation: (student) => student.status === StudentStatus.dropped || student.status === StudentStatus.program_concluded,
  },
};

// Schema for bulk updating specific students by ID
export const bulkUpdateSpecificStudentsOptionsSchema = z.object({
  studentIds: z.array(z.string()),
  options: updateStudentOptionsSchema,
  actionName: updateStudentsBulkActionsSchema.optional(),
});

// Schema for bulk updating all students matching filter criteria
export const bulkUpdateAllStudentsOptionsSchema = z.object({
  filters: getStudentsFilterSchema,
  options: updateStudentOptionsSchema,
  actionName: updateStudentsBulkActionsSchema.optional(),
});

// Union of both schemas - exactly one must be used at a time
export const bulkUpdateStudentsOptionsSchema = z.union([
  bulkUpdateSpecificStudentsOptionsSchema,
  bulkUpdateAllStudentsOptionsSchema,
]);
export type BulkUpdateStudentsOptions = z.infer<typeof bulkUpdateStudentsOptionsSchema>;

export const bulkAssignReturningStudentsOptionsSchema = z.object({
  studentIds: z.array(z.string()).min(1),
});
export type BulkAssignReturningStudentsOptions = z.infer<typeof bulkAssignReturningStudentsOptionsSchema>;

export const bulkAssignReturningStudentsResultsSchema = z.object({
  updatedCount: z.number().int(),
  skippedCount: z.number().int(),
});
export type BulkAssignReturningStudentsResults = z.infer<typeof bulkAssignReturningStudentsResultsSchema>;

//#region GLA Record schemas

export const studentWithGLARecordSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  grade: studentYearNullableSchema,
  glaFollowUpStatus: glaFollowUpStatusSchema.nullable(),
  glaReviewNotes: z.string().nullable(),
  glaReviewer: z.lazy(() => userLikeSchema).nullable(),
  userRoles: z.array(userRoleWithSchoolSchema),
  onDemandGLAs: z.array(z.lazy(() => onDemandGLAWithSessionFeedbackSchema)),
  completedGLASessions: z.array(
    z.object({
      sessionId: z.string(),
      coachId: z.string(),
      studentId: z.string(),
      startInstant: z.instanceof(Temporal.Instant),
    }),
  ),
  upcomingRegularGLASessions: z.array(
    z.object({
      sessionId: z.string(),
      startInstant: z.instanceof(Temporal.Instant),
    }),
  ),
});
export type StudentWithGLARecord = z.infer<typeof studentWithGLARecordSchema>;

export const updateStudentGLAOptionsSchema = z.object({
  glaFollowUpStatus: glaFollowUpStatusSchema,
  glaReviewerId: z.string().nullable().optional(),
  glaReviewNotes: z.string().nullable().optional(),
});
export type UpdateStudentGLAOptions = z.infer<typeof updateStudentGLAOptionsSchema>;

export const getStudentsWithGLARecordsFilterSchema = z.object({
  query: z.string().optional(),
  schoolIds: z.array(z.string()).optional(),
  grades: z.array(studentYearSchema).optional(),
});
export type GetStudentsWithGLARecordsFilter = z.infer<typeof getStudentsWithGLARecordsFilterSchema>;

export const getStudentsWithGLARecordsSortableColumnSchema = z.enum([
  'onDemandGLACount',
  'schoolName',
  'grade',
  'fullNameOrEmail',
]);
export type GetStudentsWithGLARecordsSortableColumn = z.infer<typeof getStudentsWithGLARecordsSortableColumnSchema>;

export const getStudentsWithGLARecordsSortOrderSchema = sortOrderSchemaFrom(
  getStudentsWithGLARecordsSortableColumnSchema,
);
export type GetStudentsWithGLARecordsSortOrder = z.infer<typeof getStudentsWithGLARecordsSortOrderSchema>;

export const getStudentsWithGLARecordsModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getStudentsWithGLARecordsFilterSchema))
  .merge(sortingSchemaFrom(getStudentsWithGLARecordsSortableColumnSchema));
export type GetStudentsWithGLARecordsModifier = z.infer<typeof getStudentsWithGLARecordsModifierSchema>;

export const getStudentsWithGLARecordsResultsSchema = paginatedResponse.extend({
  students: z.array(studentWithGLARecordSchema),
});
export type GetStudentsWithGLARecordsResults = z.infer<typeof getStudentsWithGLARecordsResultsSchema>;

const optionalEmailCsvField = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((val): string | null => {
    if (val == null) return null;
    const trimmed = val.trim();
    if (trimmed === '') return null;
    return trimmed.toLowerCase();
  })
  .pipe(z.string().email('Invalid email').nullable());

const optionalFleidCsvField = z
  .string()
  .trim()
  .nullable()
  .optional()
  .or(z.literal(''))
  .refine((val) => !val || val.trim() === '' || /^[Ff][Ll]\d{12}$/.test(val.trim()), {
    message: 'Unrecognized FLEID format',
  })
  .transform((val): string | null => {
    if (!val || val.trim() === '') return null;
    return val.trim().toUpperCase();
  });

const optionalStudentIdCsvField = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((val): string | null => {
    if (val == null) return null;
    const trimmed = val.trim();
    return trimmed === '' ? null : trimmed;
  })
  .pipe(z.string().uuid('Invalid student ID').nullable());

/** Header spellings are matched ignoring case, spaces, and punctuation, so `Starting Lesson` === `startingLesson`. */
const normalizeCsvHeaderKey = (header: string): string => header.toLowerCase().replace(/[^a-z0-9]/g, '');

const bulkUpdateLearningPathCsvHeaders: Record<string, string> = {
  [normalizeCsvHeaderKey('email')]: 'email',
  [normalizeCsvHeaderKey('fleid')]: 'fleid (optional)',
  [normalizeCsvHeaderKey('fleid (optional)')]: 'fleid (optional)',
  [normalizeCsvHeaderKey('studentId')]: 'studentId',
  [normalizeCsvHeaderKey('studentId (optional)')]: 'studentId',
  [normalizeCsvHeaderKey('startingLesson')]: 'startingLesson',
};

function canonicalizeBulkUpdateLearningPathCsvHeaders(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }

  const row: Record<string, unknown> = {};
  for (const [header, cell] of Object.entries(value)) {
    const key = bulkUpdateLearningPathCsvHeaders[normalizeCsvHeaderKey(header)] ?? header;
    if (row[key] == null || row[key] === '') {
      row[key] = cell;
    }
  }

  return row;
}

/** CSV columns for bulk updating student learning path & starting lesson.
 * Headers match the create-student CSV template, plus `startingLesson`.
 * Extra template columns (firstName, grade, etc.) are ignored.
 */
export const bulkUpdateStudentLearningPathCsvRow = z.preprocess(
  canonicalizeBulkUpdateLearningPathCsvHeaders,
  z
    .object({
      email: optionalEmailCsvField,
      ['fleid (optional)']: optionalFleidCsvField,
      studentId: optionalStudentIdCsvField,
      startingLesson: z
        .union([z.string(), z.null(), z.undefined()])
        .transform((val) => (val ?? '').trim())
        .pipe(z.string().min(1, 'Starting lesson is required')),
    })
    .superRefine((data, ctx) => {
      if (!data.email && !data['fleid (optional)'] && !data.studentId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Either Email, FLEID, or student ID is required',
          path: ['email'],
        });
      }
    }),
);
export type BulkUpdateStudentLearningPathCsvRow = z.infer<typeof bulkUpdateStudentLearningPathCsvRow>;

export const BulkUpdateStudentLearningPathCsvFormKeys = {
  csvFile: 'csvFile',
  schoolId: 'schoolId',
} as const;

/** `schoolId` is sent only from a school roster, where the update must stay scoped to that school. */
export const bulkUpdateStudentLearningPathCsvBody = z.object({
  [BulkUpdateStudentLearningPathCsvFormKeys.schoolId]: z.string().uuid().optional(),
});

export type BulkUpdateStudentLearningPathCsvResult = CSVUploadResponse;

//#endregion
